import { useEffect, useState } from "react";
import {
    Backdrop, Box, Card, CardContent, CircularProgress, Container, Dialog,
    DialogActions, DialogContent, DialogContentText, DialogTitle, Divider,
    Grid, ThemeProvider, Typography, createTheme, styled
} from "@mui/material";
import { StaticDatePicker } from '@mui/x-date-pickers/StaticDatePicker';
import dayjs, { Dayjs } from "dayjs";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { LoadingButton } from "@mui/lab";
import { btnStyle } from "../user/Tabs/utilisateur/UtilisateurRow";
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import SaveIcon from '@mui/icons-material/Save';
import { AlertType } from "../../../../types/AlertType";
import { SnackBarAlert } from "../../../element/SnackBarAlert";
import { frFR } from '@mui/x-date-pickers/locales';
import "dayjs/locale/fr";
import Cookies from "universal-cookie";
import axios, { AxiosInstance } from "axios";

interface DateLock {
    prevValue?: Dayjs | null;
    dateVerrouillage?: Dayjs | null;
}

interface DateCloture {
    prevValue?: Dayjs | null;
    dateCloture?: Dayjs | null;
}

const StyledStaticDatePicker = styled(StaticDatePicker)({
    '.css-8pgiau-MuiButtonBase-root-MuiPickersDay-root.Mui-selected': {
        backgroundColor: '#ff5733 !important'
    },
    '.css-1hbyad5-MuiTypography-root': {
        textTransform: 'none'
    }
});

const theme = createTheme(
    {
        palette: {
            primary: { main: '#1976d2' },
        },
    },
    frFR, // use 'fr' locale for UI texts (start, next month, ...)
);

const urlBaseApi = process.env.REACT_APP_BASE_API_URL as string;

const DateVerrouillageClotureBudget = () => {
    const cookies = new Cookies();
    const token: string = cookies.get("jwt");

    const http: AxiosInstance = axios.create({
        baseURL: urlBaseApi,
        headers: {
            Authorization: `Bearer ${token}`,
        },
    });

    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [isClickSave, setIsClickSave] = useState<boolean>(false);
    const [openDateVerrouillageModal, setOpenDateVerrouillageModal] = useState<boolean>(false);
    const [openDateClotureModal, setOpenDateClotureModal] = useState<boolean>(false);

    const date = new Date();
    const [selectedYear, setSelectedYear] = useState<Dayjs | null>(dayjs(date));

    const [dateVerrouillageToSave, setDateVerrouillageToSave] = useState<DateLock>();
    const [dateClotureToSave, setDateClotureToSave] = useState<DateCloture>();

    const [openSnack, setOpenSnack] = useState<boolean>(false);
    const [alertMessage, setAlerteMessage] = useState<string>("");
    const [alertType, setAlertType] = useState<AlertType>("success");

    const getDefaultDateVerrouillageBudget = async () => {
        setIsLoading(true);
        await http.get(`date-verrouillage-budget/${selectedYear?.format('YYYY')}`)
            .then(res => {
                setDateVerrouillageToSave({
                    ...dateVerrouillageToSave,
                    prevValue: null,
                    dateVerrouillage: res.data ? dayjs(res.data.date_verouillage_budget) : null
                });
            }).catch(error => {
                console.log(error);
            }).finally(() => {
                setIsLoading(false);
            });
    };

    const getDefaultDateClotureBudget = async () => {
        setIsLoading(true);
        await http.get(`date-cloture-budget/${selectedYear?.format('YYYY')}`)
            .then(res => {
                setDateClotureToSave({
                    ...dateClotureToSave,
                    prevValue: null,
                    dateCloture: res.data ? dayjs(res.data.date_cloture_budget) : null
                })
            }).catch(error => {
                console.log(error);
            }).finally(() => {
                setIsLoading(false);
            });
    };

    useEffect(() => {
        if (selectedYear) {
            getDefaultDateVerrouillageBudget();
            getDefaultDateClotureBudget();
        }
    }, [selectedYear]);

    const handleChangeYear = (e: any) => {
        setSelectedYear(dayjs(e));
    };

    const handleChangeDateVerrouillage = (e: any) => {
        const prevDay = dateVerrouillageToSave ? dateVerrouillageToSave?.dateVerrouillage?.format('DD') : null;

        const curr = dateVerrouillageToSave?.dateVerrouillage;

        if (e) {
            if (Number(prevDay) === Number(e.format('DD'))) {
                setDateVerrouillageToSave({
                    ...dateVerrouillageToSave,
                    prevValue: curr,
                    dateVerrouillage: null
                });
            } else {
                setDateVerrouillageToSave({
                    ...dateVerrouillageToSave,
                    prevValue: curr,
                    dateVerrouillage: e
                });

                setOpenDateVerrouillageModal(true);
            }
        }
    };

    const handleChangeDateCloture = (e: any) => {
        const prevDay = dateClotureToSave ? dateClotureToSave?.dateCloture?.format('DD') : null;

        const curr = dateClotureToSave?.dateCloture;

        if (e) {
            if (Number(prevDay) === Number(e.format('DD'))) {
                setDateClotureToSave({
                    ...dateClotureToSave,
                    prevValue: curr,
                    dateCloture: null
                });
            } else {
                setDateClotureToSave({
                    ...dateClotureToSave,
                    prevValue: curr,
                    dateCloture: e
                });

                setOpenDateClotureModal(true);
            }
        }
    };

    const handleCloseDateVerrouillageModal = () => {
        const prev = dateVerrouillageToSave?.prevValue;
        setDateVerrouillageToSave({
            prevValue: null,
            dateVerrouillage: prev
        });

        setOpenDateVerrouillageModal(false);
    }

    const handleCloseDateClotureModal = () => {
        const prev = dateClotureToSave?.prevValue;

        setDateClotureToSave({
            prevValue: null,
            dateCloture: prev
        });

        setOpenDateClotureModal(false);
    }

    const handleSaveDateVerrouillage = async () => {
        if (dateVerrouillageToSave) {
            setIsClickSave(true);

            const data = {
                dateVerrouillage: dateVerrouillageToSave?.dateVerrouillage?.format('YYYY-MM-DD'),
                selectedYear: selectedYear?.format('YYYY')
            };

            await http.post('date-verrouillage-budget/save', data)
                .then(() => {
                    setAlerteMessage("Enregistrement efféctuée !");
                    setAlertType("success");
                    setOpenSnack(true);
                    setOpenDateVerrouillageModal(false);
                }).catch(error => {
                    console.log(error);
                }).finally(() => {
                    setIsClickSave(false);
                });
        } else {
            setAlerteMessage("Merci de selectionné une date de verouillage pour l'année selectionné !");
            setAlertType("success");
            setOpenSnack(true);
        }
    }

    const handleSaveDateCloture = async () => {
        if (dateClotureToSave) {
            setIsClickSave(true);

            const data = {
                dateCloture: dateClotureToSave?.dateCloture?.format('YYYY-MM-DD'),
                selectedYear: selectedYear?.format('YYYY')
            };

            await http.post('date-cloture-budget/save', data)
                .then(() => {
                    setAlerteMessage("Enregistrement efféctuée !");
                    setAlertType("success");
                    setOpenSnack(true);
                    setOpenDateClotureModal(false);
                }).catch(error => {
                    console.log(error);
                }).finally(() => {
                    setIsClickSave(false);
                });
        } else {
            setAlerteMessage("Merci de selectionné une date de clôture pour l'année selectionné !");
            setAlertType("success");
            setOpenSnack(true);
        }
    }

    const handleCloseAlert = () => {
        setOpenSnack(false);
    };

    const maxDate = new Date(new Date().setFullYear(new Date().getFullYear() + 1));

    // S'il y a date de cloture, alors la date de verrouillage ne doit pas être supérieur à ce date de cloture
    const minDateOfDateVerrouillage = dayjs(new Date(new Date().getFullYear(), 0, 1));
    const maxDateOfDateVerrouillage = dateClotureToSave?.dateCloture ? dateClotureToSave?.dateCloture.add(-1, 'day') : dayjs(new Date((new Date().getFullYear() + 1), 11, 31));

    // S'il y a date de verrouillage, alors la date de cloture doit être supérieur à ce date de verrouillage
    const minDateOfDateCloture = dateVerrouillageToSave?.dateVerrouillage ? dateVerrouillageToSave?.dateVerrouillage.add(1, 'day') : dayjs(new Date(new Date().getFullYear(), 0, 1));
    const maxDateOfDateCloture = dayjs(new Date((new Date().getFullYear() + 1), 11, 31));

    const stackAlert: JSX.Element = SnackBarAlert(openSnack, handleCloseAlert, alertType, alertMessage);

    return (
        <>
            <Container maxWidth="lg">
                <Card variant="outlined" sx={{ width: '100%' }}>
                    <CardContent>
                        <Box my={2}>
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DatePicker
                                    label="Année Budget..."
                                    views={["year"]}
                                    value={selectedYear}
                                    onChange={handleChangeYear}
                                    minDate={dayjs(date)}
                                    maxDate={dayjs(maxDate)}
                                    slotProps={{
                                        textField: {
                                            color: "warning",
                                            fullWidth: true
                                        },
                                    }}
                                />
                            </LocalizationProvider>
                        </Box>
                        <Grid
                            container
                            spacing={5}
                            columns={{ xs: 6, sm: 12, md: 12 }}

                        >
                            <Grid item xs={6} sm={6} md={6}>
                                <Divider sx={{ my: 3 }}>
                                    <Typography variant="h5" component="p" gutterBottom>
                                        Date verrouillage budget
                                    </Typography>
                                </Divider>

                                <Box
                                    display="flex"
                                    justifyContent="center"
                                >
                                    <ThemeProvider theme={theme}>
                                        <LocalizationProvider adapterLocale="fr" dateAdapter={AdapterDayjs}>
                                            <StyledStaticDatePicker
                                                orientation="landscape"
                                                minDate={minDateOfDateVerrouillage}
                                                maxDate={maxDateOfDateVerrouillage}
                                                value={dateVerrouillageToSave?.dateVerrouillage}
                                                onChange={handleChangeDateVerrouillage}
                                                slotProps={{ actionBar: { actions: [] } }}
                                            />
                                        </LocalizationProvider>
                                    </ThemeProvider>
                                </Box>
                            </Grid>

                            <Grid item xs={6} sm={6} md={6}>
                                <Divider sx={{ my: 3 }}>
                                    <Typography variant="h5" component="p" gutterBottom>
                                        Date clôture budget
                                    </Typography>
                                </Divider>

                                <Box
                                    display="flex"
                                    justifyContent="center"
                                >
                                    <ThemeProvider theme={theme}>
                                        <LocalizationProvider
                                            adapterLocale="fr"
                                            dateAdapter={AdapterDayjs}
                                        >
                                            <StyledStaticDatePicker
                                                orientation="landscape"
                                                minDate={minDateOfDateCloture}
                                                maxDate={maxDateOfDateCloture}
                                                value={dateClotureToSave?.dateCloture}
                                                onChange={handleChangeDateCloture}
                                                slotProps={{ actionBar: { actions: [] } }}
                                            />
                                        </LocalizationProvider>
                                    </ThemeProvider>
                                </Box>
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>
            </Container>

            <Dialog
                open={openDateVerrouillageModal}
                keepMounted
                onClose={handleCloseDateVerrouillageModal}
                aria-describedby="alert-dialog-slide-description"
            >
                <DialogTitle>Confirmation date de verrouillage budget</DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-slide-description">
                        {`La date ${dateVerrouillageToSave?.dateVerrouillage?.format('DD-MM-YYYY')} sera la date de
                        Verrouillage pour l'année ${selectedYear?.format('YYYY')}. Etes-vous sûr de cela SVP ?`}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <LoadingButton
                        onClick={() => handleSaveDateVerrouillage()}
                        startIcon={<SaveIcon />}
                        loading={isClickSave}
                        loadingPosition="end"
                        sx={btnStyle({
                            bgColor: "#FC8404",
                            hoverColor: "#FF9456"
                        })}
                        variant="contained"
                    >
                        Oui
                    </LoadingButton>
                    <LoadingButton
                        onClick={() => handleCloseDateVerrouillageModal()}
                        startIcon={<ExitToAppIcon />}
                        sx={{ textTransform: "none" }}
                        loadingPosition="end"
                        variant="contained"
                        color="inherit"
                    >
                        Non
                    </LoadingButton>
                </DialogActions>
            </Dialog>

            <Dialog
                open={openDateClotureModal}
                keepMounted
                onClose={handleCloseDateClotureModal}
                aria-describedby="alert-dialog-slide-description"
            >
                <DialogTitle>Confirmation date de clôture budget</DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-slide-description">
                        {`La date ${dateClotureToSave?.dateCloture?.format('DD-MM-YYYY')} sera la date de
                        clôture pour l'année ${selectedYear?.format('YYYY')}. Etes-vous sûr de cela SVP ?`}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <LoadingButton
                        onClick={() => handleSaveDateCloture()}
                        startIcon={<SaveIcon />}
                        loading={isClickSave}
                        loadingPosition="end"
                        sx={btnStyle({
                            bgColor: "#FC8404",
                            hoverColor: "#FF9456"
                        })}
                        variant="contained"
                    >
                        Oui
                    </LoadingButton>
                    <LoadingButton
                        onClick={() => handleCloseDateClotureModal()}
                        startIcon={<ExitToAppIcon />}
                        sx={{ textTransform: "none" }}
                        loadingPosition="end"
                        variant="contained"
                        color="inherit"
                    >
                        Non
                    </LoadingButton>
                </DialogActions>
            </Dialog>

            <div>
                <Backdrop
                    sx={{ color: "white", zIndex: (theme) => theme.zIndex.drawer + 1 }}
                    open={isLoading}
                >
                    <CircularProgress color="inherit" />
                </Backdrop>
            </div>

            {stackAlert}
        </>
    );
};

export default DateVerrouillageClotureBudget;
